import { Form, Input } from 'antd';
import { Select } from '@components/antd';
import formatToPhoneNumber from '@shared/formatToPhoneNumber';
import formatPhoneNumber from '@shared/formatPhoneNumber';
import { CurrentUser } from '@root/types';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useEnums from '@shared/useEnums';
import Metric from '@components/shared/Metric';
import OnlyInMobile from '@components/shared/OnlyInMobile';
import OnlyInDesktopTablet from '@components/shared/OnlyInDesktopTablet';
import s from '../AccountSettings.module.less';

type Props = {
  currentUser: CurrentUser;
  handleSubmit: (any) => void;
  getValidationStatusFor: (
    string,
  ) => '' | 'success' | 'warning' | 'error' | 'validating' | undefined;
};

const ProfessionalInfoForm = ({ currentUser, handleSubmit, getValidationStatusFor }: Props) => {
  const { t } = useTranslation('accountSettings');
  const flags = useFlags();

  const { markets, changableRoles } = useEnums();
  const publicMarkets = markets
    .filter(market => market.isPublic)
    .sort((market1, market2) =>
      t(`common:markets.${market1.id}`).localeCompare(t(`common:markets.${market2.id}`)),
    );

  /* 5/11/20 We're not using a button component here because there's a bug
      with AntD's Affix. Current version (4.0.4) cannot render functional components,
      so I opted to use a regular old button here. Once we update AntD to 4.2.1, this
      can use our button component and the css will need to be cleaned up.
  */
  const saveUpdatesButton = (
    <button className={s.submitButton} type="submit">
      {t('saveUpdates')}
    </button>
  );

  return (
    <>
      <Metric className={s.accountItem} label={t('workEmail')} value={currentUser.email} />
      <Form
        onFinish={values => {
          handleSubmit({ ...currentUser, ...values });
        }}
        layout="vertical"
        data-testid="accountSettingsForm"
        initialValues={{
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          jobTitle: currentUser.jobTitle,
          phoneNumber: flags['phone-number-format']
            ? t('common:formattedPhone', { formattedPhone: currentUser.phoneNumber })
            : currentUser.formattedPhoneNumber,
          marketSlug: currentUser.marketSlug,
          role: currentUser.role,
        }}
      >
        <div className={s.inputGrid}>
          <Form.Item
            label={t('firstName')}
            name="firstName"
            validateStatus={getValidationStatusFor('first_name')}
            className={s.input}
          >
            <Input disabled size="large" />
          </Form.Item>
          <Form.Item
            label={t('lastName')}
            name="lastName"
            validateStatus={getValidationStatusFor('last_name')}
            className={s.input}
          >
            <Input disabled size="large" />
          </Form.Item>
          <Form.Item
            label={t('mobilePhone')}
            name="phoneNumber"
            normalize={flags['phone-number-format'] ? formatPhoneNumber : formatToPhoneNumber}
            validateStatus={getValidationStatusFor('phone_number')}
            className={s.input}
          >
            <Input disabled size="large" />
          </Form.Item>

          <Form.Item
            label={t('market')}
            name="marketSlug"
            validateStatus={getValidationStatusFor('market_slug')}
            className={s.input}
          >
            <Select>
              {publicMarkets.map(market => (
                <Select.Option value={market.id} key={market.id}>
                  {flags['public-london-market']
                    ? t(`common:markets.${market.id}`)
                    : t(`common:deprecatedMarkets.${market.id}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('jobTitle')}
            name="jobTitle"
            validateStatus={getValidationStatusFor('job_title')}
            className={s.input}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            label={t('professionalRole')}
            name="role"
            validateStatus={getValidationStatusFor('role')}
            className={s.input}
          >
            <Select disabled={currentUser.role === 'admin'}>
              {changableRoles.map(role => (
                <Select.Option value={role} key={role}>
                  {t(`common:roles.${role}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>
        <Form.Item>
          <OnlyInMobile>
            <div className={s.submitButtonPositioner}>{saveUpdatesButton}</div>
          </OnlyInMobile>
          <OnlyInDesktopTablet>{saveUpdatesButton}</OnlyInDesktopTablet>
        </Form.Item>
      </Form>
    </>
  );
};

export default ProfessionalInfoForm;
